<template>
    <v-dialog
        :value="value"
        :width="largura"
        persistent
        no-click-animation
        @input="$emit('input', $event)"
    >
        <div
            v-if="produtoSelecionado"
            class="pd"
            :style="{ height: altura + 'px' }"
        >
            <!-- ── Cabeçalho: quem sou eu, onde estou, quanto falta ────── -->
            <header class="pd__topo">
                <div class="pd__thumb">
                    <v-img :src="getUrl(produtoSelecionado.imagem)" eager height="100%" />
                </div>

                <div class="pd__topo-txt">
                    <div class="pd__topo-nome">{{ produtoSelecionado.descricao }}</div>
                    <div class="pd__topo-passo">{{ legendaDaTela }}</div>
                </div>

                <!-- Trilha de progresso: só faz sentido quando há etapas. -->
                <div v-if="temEtapas" class="pd__trilha">
                    <span
                        v-for="(t, i) in telas"
                        :key="i"
                        class="pd__trilha-seg"
                        :class="{
                            'pd__trilha-seg--feito': i < telaAtual,
                            'pd__trilha-seg--agora': i === telaAtual,
                        }"
                    />
                </div>

                <v-btn icon large class="pd__fechar" @click="fechar">
                    <v-icon large>mdi-close</v-icon>
                </v-btn>
            </header>

            <!-- ── Corpo ──────────────────────────────────────────────── -->

            <!-- Tela do produto -->
            <section v-if="tela.tipo === 'produto'" class="pd__corpo pd__produto">
                <!--
                    `cover` e não `contain`: o container já é arredondado, mas com
                    `contain` a foto ficava encaixotada dentro dele e os cantos caíam na
                    área transparente — o que se via era a foto retangular de cantos retos.
                    Preenchendo o quadrado, o recorte do container aparece. De quebra, o
                    enquadramento passa a ser o mesmo do card, que também corta.
                -->
                <div class="pd__foto" :style="{ width: ladoDaFoto + 'px', height: ladoDaFoto + 'px' }">
                    <v-img :src="getUrl(produtoSelecionado.imagem)" eager height="100%" cover />
                </div>

                <div class="pd__produto-txt">
                    <h2 class="pd__produto-nome">{{ produtoSelecionado.descricao }}</h2>
                    <p v-if="produtoSelecionado.detalhes" class="pd__produto-det">
                        {{ produtoSelecionado.detalhes }}
                    </p>
                    <div v-if="precoExibido" class="pd__produto-preco">{{ precoExibido }}</div>

                    <!-- Sem etapas, esta é a única tela: as observações moram aqui. -->
                    <v-text-field
                        v-if="!temEtapas"
                        v-model="produtoSelecionado.observacao"
                        :label="$t('tablet.observacoes')"
                        :placeholder="$t('tablet.observacoesExemplo')"
                        outlined
                        dense
                        hide-details
                        class="mt-5"
                    />
                </div>
            </section>

            <!-- Tela de etapa -->
            <section v-else-if="tela.tipo === 'etapa'" class="pd__corpo pd__etapa">
                <div class="pd__etapa-cab">
                    <div>
                        <div v-if="tela.etapa.contexto" class="pd__etapa-ctx">
                            {{ tela.etapa.contexto }}
                        </div>
                        <div class="pd__etapa-tit">{{ tela.etapa.passo.titulo }}</div>
                        <div class="pd__etapa-sub">{{ instrucaoDaEtapa(tela.etapa.passo) }}</div>
                    </div>
                    <EtapaSelo :passo="tela.etapa.passo" />
                </div>

                <div class="pd__opcoes custom-scrollbar">
                    <EtapaOpcao
                        v-for="(comp, cIndex) in tela.etapa.passo.complementos"
                        :key="cIndex"
                        :passo="tela.etapa.passo"
                        :complemento="comp"
                        @tocar="tocar(tela.etapa.passo, $event)"
                        @somar="somar(tela.etapa.passo, $event)"
                        @remover="remover($event)"
                    />
                </div>
            </section>

            <!-- Tela de revisão -->
            <section v-else class="pd__corpo pd__revisao">
                <div class="pd__revisao-lista custom-scrollbar">
                    <div class="pd__revisao-tit">{{ $t('tablet.confiraPedido') }}</div>

                    <div class="pd__linha pd__linha--base">
                        <span class="pd__linha-nome">{{ produtoSelecionado.descricao }}</span>
                        <!-- Sem valor, célula vazia: nem "R$ 0,00" nem travessão. -->
                        <span class="pd__linha-val">
                            {{ precoBase ? formatMoney(precoBase) : '' }}
                        </span>
                    </div>

                    <template v-for="(e, i) in etapas">
                        <div
                            v-if="escolhidosDaEtapa(e.passo).length"
                            :key="i"
                            class="pd__revisao-bloco"
                        >
                            <div class="pd__revisao-etapa">
                                {{ e.contexto ? e.contexto + ' · ' : '' }}{{ e.passo.titulo }}
                                <button type="button" class="pd__editar" @click="irParaEtapa(i)">
                                    {{ $t('envio.alterar') }}
                                </button>
                            </div>
                            <div
                                v-for="(c, j) in escolhidosDaEtapa(e.passo)"
                                :key="j"
                                class="pd__linha"
                            >
                                <span class="pd__linha-nome">
                                    {{ c.descricao }}
                                    <template v-if="+c.quantidade > 1">×{{ c.quantidade }}</template>
                                </span>
                                <span class="pd__linha-val">
                                    {{ +c.valor ? formatMoney(+c.valor * +c.quantidade) : '' }}
                                </span>
                            </div>
                        </div>
                    </template>

                    <div v-if="!algumaEscolha" class="pd__revisao-vazio">
                        {{ $t('tablet.semPersonalizacao') }}
                    </div>
                </div>

                <div class="pd__revisao-obs">
                    <v-text-field
                        v-model="produtoSelecionado.observacao"
                        :label="$t('tablet.observacoes')"
                        :placeholder="$t('tablet.observacoesExemplo')"
                        outlined
                        dense
                        hide-details
                    />
                </div>
            </section>

            <!-- ── Rodapé ─────────────────────────────────────────────── -->
            <footer class="pd__rodape">
                <ContadorQtd
                    :valor="quantidade"
                    @menos="quantidade = quantidade - 1"
                    @mais="quantidade = quantidade + 1"
                />

                <div class="pd__total">
                    <div class="pd__total-rot">{{ $t('envio.total') }}</div>
                    <div class="pd__total-val">{{ formatMoney(total) }}</div>
                </div>

                <v-btn text large color="grey darken-1" @click="voltar">
                    {{ telaAtual === 0 ? $t('geral.cancelar') : $t('tablet.voltar') }}
                </v-btn>

                <v-btn
                    color="primary"
                    x-large
                    class="pd__cta"
                    :disabled="!!travaDaTela"
                    @click="avancar"
                >
                    <v-icon left>{{ ehUltimaTela ? 'mdi-cart-plus' : 'mdi-arrow-right' }}</v-icon>
                    {{ travaDaTela || rotuloDoBotao }}
                    <span v-if="!travaDaTela && ehUltimaTela" class="pd__cta-valor">
                        {{ formatMoney(total) }}
                    </span>
                </v-btn>
            </footer>
        </div>
    </v-dialog>
</template>

<script>
import ContadorQtd from './ContadorQtd';
import EtapaOpcao from '@/components/EtapaOpcao';
import EtapaSelo from '@/components/EtapaSelo';
import { clone } from '@/utils/utils';
import { subtotal, valorTotalComplementos } from '@/utils/calc';
import {
    escolhidosDaEtapa,
    etapasDoProduto,
    instrucaoDaEtapa,
    limparEscolhasVazias,
    pendenciaDaEtapa,
    remover,
    somar,
    tocar,
    zerarEscolhas,
} from '@/utils/etapa';

export default {
    name: 'ProdutoDialog',

    components: { ContadorQtd, EtapaOpcao, EtapaSelo },

    props: {
        value: { type: Boolean, default: false },
        produto: { type: Object, default: null },
        alturaJanela: { type: Number, default: 600 },
    },

    data: () => ({
        produtoSelecionado: null,
        telaAtual: 0,
    }),

    computed: {
        altura() {
            const tetoDoVuetify = Math.floor(this.alturaJanela * 0.9) - 2;
            return Math.max(380, Math.min(tetoDoVuetify, 720));
        },

        largura() {
            return Math.min(940, Math.max(600, this.alturaJanela * 1.7));
        },

        ladoDaFoto() {
            return Math.round(Math.min(300, Math.max(170, this.altura * 0.5)));
        },

        etapas() {
            return etapasDoProduto(this.produtoSelecionado);
        },

        temEtapas() {
            return this.etapas.length > 0;
        },

        telas() {
            const t = [{ tipo: 'produto' }];
            this.etapas.forEach(etapa => t.push({ tipo: 'etapa', etapa }));
            if (this.temEtapas) t.push({ tipo: 'revisao' });
            return t;
        },

        tela() {
            return this.telas[this.telaAtual] || this.telas[0];
        },

        ehUltimaTela() {
            return this.telaAtual === this.telas.length - 1;
        },

        legendaDaTela() {
            if (this.tela.tipo === 'produto') {
                return this.temEtapas ? this.$t('tablet.confiraProduto') : this.$t('tablet.adicioneAoCarrinho');
            }
            if (this.tela.tipo === 'revisao') return this.$t('tablet.revisaoPedido');
            return `${this.$t('etapa.progresso', { atual: this.telaAtual, total: this.etapas.length })} · ${this.tela.etapa.passo.titulo}`;
        },

        rotuloDoBotao() {
            if (this.ehUltimaTela) return this.$t('tablet.adicionar');
            if (this.tela.tipo === 'produto') return this.$t('tablet.personalizar');
            return this.$t('geral.continuar');
        },

        travaDaTela() {
            if (this.tela.tipo !== 'etapa') {
                if (this.ehUltimaTela) {
                    const pendente = this.etapas.find(e => this.pendenciaDaEtapa(e.passo));
                    if (pendente) return this.$t('tablet.falta', { etapa: pendente.passo.titulo });
                }
                return null;
            }
            return this.pendenciaDaEtapa(this.tela.etapa.passo);
        },

        precoBase() {
            const p = this.produtoSelecionado;
            return +(p?.valor || p?.valor_minimo || 0);
        },

        precoExibido() {
            const p = this.produtoSelecionado;

            if (!p) return '';
            if (+p.valor_minimo) return this.$t('produto.aPartirDe', { valor: this.formatMoney(+p.valor_minimo) });
            if (+p.valor) return `${this.formatMoney(+p.valor)}`;

            return '';
        },

        quantidade: {
            get() {
                return +(this.produtoSelecionado?.quantidade || 1);
            },
            set(v) {
                this.$set(this.produtoSelecionado, 'quantidade', Math.max(1, v));
            },
        },

        total() {
            return this.produtoSelecionado ? +subtotal(this.produtoSelecionado) : 0;
        },

        algumaEscolha() {
            return this.etapas.some(e => this.escolhidosDaEtapa(e.passo).length > 0);
        },
    },

    watch: {
        value(aberto) {
            if (aberto) this.iniciar();
        },

        produto() {
            if (this.value) this.iniciar();
        },
    },

    methods: {
        iniciar() {
            const p = clone(this.produto || {});
            this.$set(p, 'quantidade', +p.quantidade || 1);
            this.$set(p, 'observacao', p.observacao || '');

            zerarEscolhas(p);

            this.produtoSelecionado = p;
            this.telaAtual = 0;
        },

        fechar() {
            this.$emit('input', false);
        },

        voltar() {
            if (this.telaAtual === 0) return this.fechar();
            this.telaAtual--;
        },

        avancar() {
            if (this.travaDaTela) return;
            if (this.ehUltimaTela) return this.finalizar();
            this.telaAtual++;
        },

        irParaEtapa(indice) {
            this.telaAtual = indice + 1;
        },


        escolhidosDaEtapa(passo) {
            return escolhidosDaEtapa(passo);
        },

        pendenciaDaEtapa(passo) {
            return pendenciaDaEtapa(passo);
        },

        instrucaoDaEtapa(passo) {
            return instrucaoDaEtapa(passo);
        },

        tocar(passo, comp) {
            tocar(passo, comp);
        },

        somar(passo, comp) {
            somar(passo, comp);
        },

        remover(comp) {
            remover(comp);
        },

        getUrl(img) {
            if (!img?.url) return require('@/assets/noimageavailable.png');

            return img.tmp
                ? `${this.$urlAPI}/files/tmp/${img.url}`
                : `${this.$urlAPI}/storage/${img.url}`;
        },

        adicionalDaEtapa(passo) {
            return +valorTotalComplementos(passo) || 0;
        },

        finalizar() {
            limparEscolhasVazias(this.produtoSelecionado);

            this.$emit('adicionar', this.produtoSelecionado);
            this.$emit('input', false);
        },
    },
};
</script>

<style scoped>
.pd {
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    position: relative;
    min-height: 0;
}
.theme--dark .pd { background: #1e1e1e; }

.pd__topo {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 66px 12px 16px;
    border-bottom: 1px solid rgba(128, 128, 128, 0.2);
    flex: none;
}
.pd__thumb {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    overflow: hidden;
    flex: none;
}
.pd__topo-txt { flex: 1; min-width: 0; }
.pd__topo-nome {
    font-size: 15px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.pd__topo-passo {
    font-size: 12.5px;
    color: #8b9098;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.pd__trilha { display: flex; gap: 4px; width: 190px; flex: none; }
.pd__trilha-seg {
    flex: 1;
    height: 6px;
    border-radius: 6px;
    background: rgba(128, 128, 128, 0.25);
    transition: background 0.2s;
}
.pd__trilha-seg--feito { background: #0f8a3d; }
.pd__trilha-seg--agora { background: var(--v-primary-base); }

.pd__fechar { position: absolute; top: 8px; right: 8px; z-index: 5; }

.pd__corpo { flex: 1; min-height: 0; display: flex; }

.pd__produto { align-items: center; gap: 26px; padding: 8px 26px 20px; }
.pd__foto { border-radius: 16px; overflow: hidden; flex: none; }
.pd__produto-txt { flex: 1; min-width: 0; }
.pd__produto-nome { font-size: 23px; font-weight: 800; line-height: 1.2; margin: 0; }
.pd__produto-det {
    font-size: 14px;
    color: #6b7178;
    line-height: 1.5;
    margin: 8px 0 0;
    white-space: pre-line;
}
.theme--dark .pd__produto-det { color: #b0b6bd; }
.pd__produto-preco {
    font-size: 25px;
    font-weight: 800;
    color: var(--v-primary-base);
    margin-top: 14px;
}

.pd__etapa { flex-direction: column; padding: 14px 22px 0; }
.pd__etapa-cab {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 12px;
    flex: none;
}
.pd__etapa-ctx {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--v-primary-base);
    margin-bottom: 2px;
}
.pd__etapa-tit { font-size: 18px; font-weight: 800; line-height: 1.2; }
.pd__etapa-sub { font-size: 13px; color: #6b7178; margin-top: 2px; }
.theme--dark .pd__etapa-sub { color: #b0b6bd; }

.pd__opcoes {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 9px;
    align-content: start;
    padding-bottom: 14px;
}
@media (max-width: 720px) {
    .pd__opcoes { grid-template-columns: minmax(0, 1fr); }
    .pd__produto { flex-direction: column; gap: 14px; text-align: center; }
}

.pd__revisao { flex-direction: column; padding: 0; }
.pd__revisao-lista {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 14px 24px 4px;
}
.pd__revisao-tit { font-size: 17px; font-weight: 800; margin-bottom: 10px; }
.pd__revisao-bloco { margin-top: 12px; }
.pd__revisao-etapa {
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 11.5px;
    font-weight: 800;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #8b9098;
    margin-bottom: 5px;
}
.pd__editar {
    font-size: 11.5px;
    font-weight: 700;
    color: var(--v-primary-base);
    text-transform: none;
    letter-spacing: 0;
    cursor: pointer;
}
.pd__linha { display: flex; justify-content: space-between; gap: 12px; font-size: 14px; padding: 3px 0; }
.pd__linha--base { font-weight: 700; padding-bottom: 8px; border-bottom: 1px dashed rgba(128,128,128,.3); }
.pd__linha-nome { color: #4a5057; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.theme--dark .pd__linha-nome { color: #c8ced5; }
.pd__linha-val { font-weight: 700; white-space: nowrap; }
.pd__revisao-vazio { font-size: 13.5px; color: #8b9098; font-style: italic; margin-top: 14px; }
.pd__revisao-obs { flex: none; padding: 10px 24px 4px; }

.pd__rodape {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 11px 20px;
    border-top: 1px solid rgba(128, 128, 128, 0.2);
    flex: none;
}
.pd__total { margin-right: auto; }
.pd__total-rot {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    font-weight: 700;
    color: #8b9098;
}
.pd__total-val { font-size: 20px; font-weight: 800; line-height: 1.1; }
.pd__cta { font-weight: 800; }
.pd__cta-valor {
    background: rgba(0, 0, 0, 0.2);
    padding: 3px 10px;
    border-radius: 8px;
    margin-left: 10px;
}

.custom-scrollbar::-webkit-scrollbar { width: 7px; }
.custom-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(128, 128, 128, 0.35);
    border-radius: 7px;
}
</style>
